import Money from '~~/models/Money'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { QUERY_ADDON_PREFIX, QUERY_SUBSCRIPTION } from '~/constants/queryParams'

import {
  EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL,
  EDITOR_PREPAID_SUBSCRIPTION_DEFAULT_DRAWS_MAX,
  EDITOR_PREPAID_SUBSCRIPTION_DEFAULT_DRAWS_MIN,
  EDITOR_TICKET_TYPE,
  type EditorTicketType,
} from '../constants/options'
import { useEditorOptionsStore } from '../stores/options'
import { isFixedCharge } from '../utils/editorCharges'

export const useEditorOptions = (lotteryId: LotteryId) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const route = useRoute()
  const { getByPrefix } = useQueryParams()
  const { removeQueryParams } = useRouterUtils()

  const store = useEditorOptionsStore(lotteryId)

  const { result } = useLotteryQuery({ licensedTerritory, lotteryId })

  const config = computed(() => result.value?.lottery.currentConfig ?? null)

  const priceList = computed(() => result.value?.lottery.priceList ?? null)

  const prepaidSubscription = computed(() => config.value?.prepaidSubscription ?? null)

  const isPrepaidSubscriptionAllowed = computed(() => !!prepaidSubscription.value)

  const isSubscriptionAllowed = computed(() => !!config.value?.subscriptionsAllowed)

  const prepaidSubscriptionCustomDrawsMax = computed(
    () =>
      prepaidSubscription.value?.customDrawsMax ?? EDITOR_PREPAID_SUBSCRIPTION_DEFAULT_DRAWS_MAX,
  )

  const prepaidSubscriptionCustomDrawsMin = computed(
    () =>
      prepaidSubscription.value?.customDrawsMin ?? EDITOR_PREPAID_SUBSCRIPTION_DEFAULT_DRAWS_MIN,
  )

  const visibleAddOns = computed(() => config.value?.addOns.filter(({ perPlay }) => !perPlay) ?? [])

  const getAddOnPricePerPlay = (name: string) => {
    const charge = priceList.value?.charges.find((charge) => charge.id === name)
    return new Money(isFixedCharge(charge) ? charge.value : 1)
  }

  const applyAddOnsFromQuery = () => {
    const params = getByPrefix(QUERY_ADDON_PREFIX)

    params.forEach(({ name, value }) => {
      const addOn = visibleAddOns.value.find(
        (addOn) => addOn.name.toLowerCase() === name.toLowerCase(),
      )
      if (!addOn) return
      override(() => store.setAddOn(addOn.name, value === 'true'))
    })

    return params.map(({ name }) => `${QUERY_ADDON_PREFIX}_${name}`)
  }

  const applyTicketTypeFromQuery = () => {
    const subscription = route.query[QUERY_SUBSCRIPTION]
    if (!subscription) return []

    const draws = Number(subscription)
    const hasDraws = !isNaN(draws) && draws > 0
    const isSubscription = subscription === 'true' || hasDraws

    override(() => {
      store.setTicketType(resolveTicketType(isSubscription))

      if (isPrepaidSubscriptionAllowed.value && isSubscription) {
        store.setPrepaidSubscriptionAmount(resolvePrepaidAmount(hasDraws ? draws : null))
      }
    })

    return [QUERY_SUBSCRIPTION]
  }

  // options set by the url win over options restored from an earlier session, field by field
  const queryOverrides: (() => void)[] = []

  const override = (apply: () => void) => {
    queryOverrides.push(apply)
    apply()
  }

  const reapplyFromQuery = () => queryOverrides.forEach((apply) => apply())

  const applyFromQuery = async () => {
    await until(config).not.toBeNull()

    queryOverrides.length = 0
    const appliedParams = [...applyAddOnsFromQuery(), ...applyTicketTypeFromQuery()]
    if (appliedParams.length) await stripQueryParams(...appliedParams)
  }

  const stripQueryParams = async (...names: string[]) => {
    try {
      await removeQueryParams(...names)
    } catch {
      return
    }
  }

  const resolvePrepaidAmount = (draws: Maybe<number>) => {
    const selectableOptions = prepaidSubscription.value?.selectableDrawsOptions ?? []

    if (draws === null) {
      return { label: `${selectableOptions[0]}`, value: selectableOptions[0] }
    }

    if (selectableOptions.includes(draws)) return { label: `${draws}`, value: draws }

    return {
      label: EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL,
      value: Math.min(
        Math.max(draws, prepaidSubscriptionCustomDrawsMin.value),
        prepaidSubscriptionCustomDrawsMax.value,
      ),
    }
  }

  const resolveTicketType = (isSubscription: boolean): EditorTicketType => {
    if (!isSubscription) return EDITOR_TICKET_TYPE.Single
    if (isPrepaidSubscriptionAllowed.value) return EDITOR_TICKET_TYPE.PrepaidSubscription
    if (isSubscriptionAllowed.value) return EDITOR_TICKET_TYPE.Subscription
    return EDITOR_TICKET_TYPE.Single
  }

  return {
    applyFromQuery,
    config,
    getAddOnPricePerPlay,
    isPrepaidSubscriptionAllowed,
    isSubscriptionAllowed,
    prepaidSubscription,
    prepaidSubscriptionCustomDrawsMax,
    prepaidSubscriptionCustomDrawsMin,
    reapplyFromQuery,
    store,
    visibleAddOns,
  }
}
